/** Where "Advanced setup" goes from each place in the easy flow (plan §7). */
import type { EasyLane } from '@/api/easyOnboarding';

import type { WizardData } from '../wizard/types';

/** The advanced create flow (the setup wizard's create mode). */
export const ADVANCED_CREATE_PATH = '/tour-operators/new/advanced';

/** What the easy flow hands the advanced create flow. Identity only — the type has no room
 *  for a credential, and `toAdvancedPrefill` copies these keys and nothing else. */
export type AdvancedPrefill = Partial<
  Pick<
    WizardData,
    'name' | 'code' | 'email' | 'phone' | 'url' | 'restech' | 'lang_enabled' | 'lang_fallback'
  >
>;

const PREFILL_KEYS = [
  'name',
  'code',
  'email',
  'phone',
  'url',
  'restech',
  'lang_enabled',
  'lang_fallback',
] as const;

/** Copies only the allow-listed identity keys (a stray `user_key` never rides along). */
export function toAdvancedPrefill(source: Record<string, unknown>): AdvancedPrefill {
  const out: Record<string, unknown> = {};
  for (const k of PREFILL_KEYS) {
    const v = source[k];
    if (typeof v === 'string' && v) out[k] = v;
    else if (Array.isArray(v) && v.every((x) => typeof x === 'string') && v.length > 0)
      out[k] = [...v];
  }
  return out as AdvancedPrefill;
}

/** Router state for `ADVANCED_CREATE_PATH`. */
export interface AdvancedCreateState {
  prefill: AdvancedPrefill;
}

/** Reads `location.state.prefill` defensively (any shape can arrive through history). */
export function prefillFromState(state: unknown): AdvancedPrefill | null {
  const prefill = (state as { prefill?: unknown } | null)?.prefill;
  if (!prefill || typeof prefill !== 'object') return null;
  return toAdvancedPrefill(prefill as Record<string, unknown>);
}

export const setupStepPath = (topId: number, step: string) =>
  `/tour-operators/${topId}/setup/${step}`;

/** The screens' own hand-offs: Choose tours → catalog, Ready → the run board. */
export const SCREEN_ADVANCED_STEP = { choose: 'catalog', ready: 'run' } as const;

/** A lane's advanced-setup step — served by the backend (`advanced_step`), so the two flows
 *  cannot drift. */
export function laneAdvancedPath(topId: number, lane: Pick<EasyLane, 'advanced_step'>): string {
  return setupStepPath(topId, lane.advanced_step);
}
